/**
 * @file Product-analytics event taxonomy — the single source of truth.
 *
 * Conventions (see docs/design-docs/posthog-analytics/posthog-analytics.html):
 *  - Event names are lowercase snake_case, past-tense `object_action`.
 *    The object noun doubles as the area prefix so PostHog's alphabetical
 *    event picker groups related events together.
 *  - Every event automatically carries `feature_area` (injected by `track()`
 *    from EVENT_FEATURE_AREA) plus the super-property envelope registered at
 *    identify time (environment, organization_id, organization_name, tenant_id).
 *  - Add a new event by adding it in THREE places below: ANALYTICS_EVENTS,
 *    AnalyticsEventProperties, and EVENT_FEATURE_AREA. TypeScript enforces the
 *    last one, so a missing entry is a compile error, not a silent gap.
 *  - Controls that repeat across surfaces (search boxes, filter/sort/tab
 *    strips) use ONE event with a `surface` property rather than one event per
 *    page — see `list_searched` / `list_filter_changed`. Those two declare
 *    their own `feature_area`, which overrides EVENT_FEATURE_AREA.
 *  - Never capture user-authored text: no search queries, file names, or
 *    prompt bodies. Capture counts, booleans, and closed enums instead.
 */

/** Product areas — the breakdown dimension for "which areas do users touch". */
export const FEATURE_AREAS = {
  chat: 'chat',
  home: 'home',
  reports: 'reports',
  routines: 'routines',
  knowledge_hub: 'knowledge_hub',
  skills: 'skills',
  integrations: 'integrations',
  settings: 'settings',
  agents: 'agents',
  onboarding: 'onboarding',
  auth: 'auth',
  glossary: 'glossary',
  navigation: 'navigation',
  tasks: 'tasks',
} as const;

export type FeatureArea = (typeof FEATURE_AREAS)[keyof typeof FEATURE_AREAS];

/** Every product event we capture. Values are the PostHog event names. */
export const ANALYTICS_EVENTS = {
  // ── cross-surface list controls ────────────────────────────────────────
  // Search and filter/sort/tab controls look the same on every list page, and
  // the question asked of them is always "does anyone use this, and where".
  // One event per control kind with a `surface` property answers that in a
  // single insight; a per-surface event would mean ~14 events and as many
  // dashboard tiles. These two carry their own `feature_area` (see below).
  LIST_SEARCHED: 'list_searched',
  LIST_FILTER_CHANGED: 'list_filter_changed',

  // ── onboarding / activation ────────────────────────────────────────────
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_ORGANIZATION_CREATED: 'onboarding_organization_created',
  ONBOARDING_COMPLETED: 'onboarding_completed',

  // ── auth / session ─────────────────────────────────────────────────────
  USER_SIGNED_OUT: 'user_signed_out',
  ORGANIZATION_SWITCHED: 'organization_switched',
  ACCOUNT_ADDED: 'account_added',

  // ── chat ───────────────────────────────────────────────────────────────
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  CHAT_THREAD_CREATED: 'chat_thread_created',
  CHAT_FILE_ATTACHED: 'chat_file_attached',
  CHAT_DICTATION_USED: 'chat_dictation_used',
  CHAT_SLASH_COMMAND_USED: 'chat_slash_command_used',
  CHAT_MODE_CHANGED: 'chat_mode_changed',
  CHAT_TOOL_APPROVED: 'chat_tool_approved',
  CHAT_TOOL_REJECTED: 'chat_tool_rejected',
  // A turn stopped driving the tool round-trip. The `reason` property is the
  // point: four states leave an identical chat row (tool calls, no outputs),
  // two of which are normal, so without it a stall cannot be counted.
  CHAT_TURN_STALLED: 'chat_turn_stalled',
  CHAT_AUTH_RETRY: 'chat_auth_retry',
  CHAT_GENERATION_STOPPED: 'chat_generation_stopped',
  CHAT_SUGGESTION_CLICKED: 'chat_suggestion_clicked',
  CHAT_THREAD_RENAMED: 'chat_thread_renamed',
  CHAT_THREAD_DELETED: 'chat_thread_deleted',
  CHAT_NEW_CHAT_CLICKED: 'chat_new_chat_clicked',

  // ── home / inbox ───────────────────────────────────────────────────────
  INBOX_CARD_OPENED: 'inbox_card_opened',
  INBOX_CARD_OPENED_IN_CHAT: 'inbox_card_opened_in_chat',
  INBOX_CARD_DISMISSED: 'inbox_card_dismissed',
  // Rows past INBOX_COLLAPSED_LIMIT hide behind a "View n more" disclosure.
  INBOX_OVERFLOW_TOGGLED: 'inbox_overflow_toggled',
  // The detail panel's expand-to-fullscreen toggle (the panel has no
  // prev/next — the artifact's drawer has none).
  INBOX_PANEL_EXPANDED: 'inbox_panel_expanded',
  // Inbox update controls (type/date filters, which replaced the tab strip).
  // `dimension` says which filter moved; `types`/`range` carry the new value.
  INBOX_FILTER_CHANGED: 'inbox_filter_changed',
  INBOX_SORT_CHANGED: 'inbox_sort_changed',
  // The standalone read/unread switch (off = unread only).
  INBOX_SHOW_READ_TOGGLED: 'inbox_show_read_toggled',

  // ── reports ────────────────────────────────────────────────────────────
  REPORT_CREATED: 'report_created',
  REPORT_RENAMED: 'report_renamed',
  REPORT_DELETED: 'report_deleted',
  REPORT_EXPORTED: 'report_exported',
  REPORT_RUN_SWITCHED: 'report_run_switched',
  REPORT_SCHEDULED: 'report_scheduled',
  // Every insight report is `/ui/reports/:id` once route_pattern collapses the
  // uuid, so pageviews cannot say WHICH report — or which kind — was opened.
  REPORT_VIEWED: 'report_viewed',
  REPORT_CREATOR_PROMPT_CLICKED: 'report_creator_prompt_clicked',
  REPORT_CUSTOMIZE_CLICKED: 'report_customize_clicked',

  // ── routines ───────────────────────────────────────────────────────────
  // Fires on intent (the button opens a chat thread); the routine itself is
  // created later by the agent, server-side. Named for what it measures.
  ROUTINE_CREATION_STARTED: 'routine_creation_started',
  ROUTINE_DRAFT_SHOWN: 'routine_draft_shown',
  ROUTINE_OPENED_IN_CHAT: 'routine_opened_in_chat',
  ROUTINE_RUN_TRIGGERED: 'routine_run_triggered',
  ROUTINE_STATUS_CHANGED: 'routine_status_changed',
  ROUTINE_DELETED: 'routine_deleted',

  // ── tasks ──────────────────────────────────────────────────────────────
  TASK_OPENED: 'task_opened',
  TASK_UPDATED: 'task_updated',
  TASK_CREATED: 'task_created',
  TASK_OPENED_IN_CHAT: 'task_opened_in_chat',
  TASK_PANEL_EXPANDED: 'task_panel_expanded',

  // ── knowledge hub ──────────────────────────────────────────────────────
  KNOWLEDGE_FILE_CREATED: 'knowledge_file_created',
  KNOWLEDGE_FILE_UPDATED: 'knowledge_file_updated',
  KNOWLEDGE_FILE_DELETED: 'knowledge_file_deleted',
  // Tree and recent-activity clicks land on the same route, so only an event
  // can tell the two entry points apart.
  KNOWLEDGE_FILE_OPENED: 'knowledge_file_opened',
  KNOWLEDGE_FILE_EDIT_STARTED: 'knowledge_file_edit_started',
  BRAND_DESIGN_SEEDED: 'brand_design_seeded',

  // ── skills ─────────────────────────────────────────────────────────────
  SKILL_CREATED: 'skill_created',
  SKILL_INSTALLED: 'skill_installed',
  SKILL_UNINSTALLED: 'skill_uninstalled',
  SKILL_UPDATED: 'skill_updated',
  SKILL_DELETED: 'skill_deleted',
  SKILL_TRIED_IN_CHAT: 'skill_tried_in_chat',

  // ── glossary ───────────────────────────────────────────────────────────
  GLOSSARY_ENTRY_OPENED: 'glossary_entry_opened',

  // ── integrations ───────────────────────────────────────────────────────
  INTEGRATION_CONNECT_STARTED: 'integration_connect_started',
  INTEGRATION_CONNECTED: 'integration_connected',
  INTEGRATION_CONNECT_FAILED: 'integration_connect_failed',
  INTEGRATION_REAUTHORIZED: 'integration_reauthorized',
  INTEGRATION_DATASET_TOGGLED: 'integration_dataset_toggled',
  INTEGRATION_PAUSED: 'integration_paused',
  INTEGRATION_SET_DEFAULT: 'integration_set_default',
  INTEGRATION_RENAMED: 'integration_renamed',
  INTEGRATION_DISCONNECTED: 'integration_disconnected',
  // Per-app config dialogs and API-key updates — the step between connecting
  // and the connection actually being usable.
  INTEGRATION_CONFIGURED: 'integration_configured',

  // ── settings ───────────────────────────────────────────────────────────
  MEMBER_INVITED: 'member_invited',
  ORGANIZATION_UPDATED: 'organization_updated',
  NOTIFICATION_CHANNEL_TOGGLED: 'notification_channel_toggled',

  // ── global chrome / navigation ─────────────────────────────────────────
  BREADCRUMB_CLICKED: 'breadcrumb_clicked',
  SIDEBAR_TOGGLED: 'sidebar_toggled',

  // ── agents ─────────────────────────────────────────────────────────────
  AGENT_INSTALLED: 'agent_installed',
  AGENT_WORKFLOW_TRIGGERED: 'agent_workflow_triggered',
  AGENT_CONFIG_SAVED: 'agent_config_saved',
  AGENT_APPROVAL_ACTIONED: 'agent_approval_actioned',
  AGENT_EXPORT_DOWNLOADED: 'agent_export_downloaded',
  AEO_VIEW_CHANGED: 'aeo_view_changed',
} as const;

export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/**
 * List surfaces that carry a search box and/or filter controls. Adding a
 * surface here is all it takes for the dashboard's "are search and filters
 * used" insight to cover it — no new event, no new tile.
 */
export type ListSurface =
  | 'chat_history'
  | 'knowledge_hub'
  | 'skills'
  | 'routines'
  | 'reports'
  | 'glossary'
  | 'members'
  | 'connections'
  | 'connection_detail'
  | 'tasks';

/** Which kind of control changed, for `list_filter_changed`. */
export type ListFilterKind =
  | 'variant'
  | 'status'
  | 'sort'
  | 'category'
  | 'tab'
  | 'date_range'
  /** A menu that narrows on several properties at once, as the Tasks board does. */
  | 'property';

/** Whether a control was reached by clicking it or by its keyboard shortcut. */
export type InteractionVia = 'input' | 'shortcut';

/** The two Knowledge Hub pages that have bespoke editors rather than markdown. */
export type StandardizedKnowledgePage = 'brand_details' | 'brand_design';

/** What caused a chat thread to be created. */
export type ChatThreadSource =
  | 'home'
  | 'new_chat'
  | 'report'
  | 'routine'
  | 'agent'
  | 'alert_card';

/** How an integration authenticates. */
export type IntegrationAuthType = 'oauth' | 'basic' | 'api_key';

/** Onboarding funnel steps, in order. */
export type OnboardingStep =
  | 'company_info'
  | 'building_profile'
  | 'brand'
  | 'competitors'
  | 'connect_data';

/**
 * Per-event property payloads. Events with no extra properties use
 * `EmptyProps` — call sites still pass `{}` so the shape stays explicit.
 */
export type EmptyProps = Record<string, never>;

export interface AnalyticsEventProperties {
  // cross-surface list controls.
  // These two are the only events that pass their own `feature_area`: they fire
  // from nine different areas, so a static entry in EVENT_FEATURE_AREA cannot
  // describe them. `track()` lets an explicit value win over the map.
  // Search *terms* are never captured — only that a search happened and
  // whether it found anything.
  list_searched: {
    surface: ListSurface;
    feature_area: FeatureArea;
    via?: InteractionVia;
    has_results?: boolean;
    result_count?: number;
  };
  list_filter_changed: {
    surface: ListSurface;
    feature_area: FeatureArea;
    filter: ListFilterKind;
    value: string;
  };

  // onboarding
  onboarding_started: EmptyProps;
  onboarding_step_completed: { step: OnboardingStep; competitor_count?: number };
  onboarding_organization_created: EmptyProps;
  onboarding_completed: {
    connected_integration_count: number;
    skipped_connect: boolean;
  };

  // auth
  user_signed_out: EmptyProps;
  organization_switched: { to_organization_id: string };
  account_added: { invite_count?: number };

  // chat
  // Captured in Composer, the one choke point both the home lobby and the
  // chat page submit through. The originating surface comes from the
  // `route_pattern` property that before_send stamps on every event.
  chat_message_sent: {
    mode?: string;
    message_length: number;
    attachment_count: number;
    used_slash_command: boolean;
    // Re-sends from the inline message editor bypass the Composer, so without
    // this they were invisible and undercounted every send metric.
    is_edit?: boolean;
  };
  chat_thread_created: { source: ChatThreadSource };
  chat_file_attached: { file_type: string; file_size_bytes: number };
  chat_dictation_used: EmptyProps;
  chat_slash_command_used: { command: string; skill_slug?: string };
  chat_mode_changed: { mode: string };
  // A single approval prompt can cover several tool calls; `tool_name` is the
  // first, which is the one the UI leads with, and `tool_count` says how many.
  chat_tool_approved: { tool_name?: string; tool_count: number };
  chat_tool_rejected: { tool_name?: string; tool_count: number };
  chat_turn_stalled: {
    reason: string;
    thread_id?: string;
    will_retry?: boolean;
    status?: number;
    pending_interactive?: number;
    tool_names?: string[];
  };
  chat_auth_retry: { thread_id?: string };
  chat_generation_stopped: { thread_id?: string };
  // `category` / `prompt_id` identify WHICH prompt was clicked — without them
  // the event only said that some suggestion somewhere was used.
  chat_suggestion_clicked: {
    source: 'starter' | 'follow_up';
    category?: string;
    prompt_id?: string;
    suggestion_index?: number;
  };
  chat_thread_renamed: EmptyProps;
  chat_thread_deleted: EmptyProps;
  chat_new_chat_clicked: { via: 'button' | 'shortcut' };

  // home / inbox
  inbox_card_opened: { card_type?: string };
  // `via` is always 'panel' now — the row kebab menu was removed with the
  // artifact-aligned cards (kept in the schema so historical rows still type).
  inbox_card_opened_in_chat: { card_type?: string; via?: 'panel' | 'row' };
  inbox_card_dismissed: { card_type?: string; via?: 'panel' | 'row' };
  // `tab` is the stream's scope ('all' | 'needs_attention'), not a tab strip —
  // the Inbox has no tabs; it says whether the gated or full inbox was open.
  inbox_overflow_toggled: { tab: string; expanded: boolean; hidden_count: number };
  // `expanded: true` is entering fullscreen, `false` is docking back.
  inbox_panel_expanded: { expanded: boolean };
  // `dimension: 'all'` is the chips band's "Clear filters"; per-dimension
  // clears carry `cleared: true`. Values are closed enums/ISO dates, never text.
  inbox_filter_changed: {
    dimension: 'type' | 'dates' | 'all';
    types?: string[];
    range?: string | null;
    from?: string | null;
    to?: string | null;
    cleared?: boolean;
  };
  inbox_sort_changed: { sort: 'latest' | 'oldest' };
  inbox_show_read_toggled: { show_read: boolean };

  // reports
  report_created: { report_type?: string };
  report_renamed: EmptyProps;
  report_deleted: EmptyProps;
  report_exported: { format: 'pdf' | 'html' };
  report_run_switched: EmptyProps;
  report_scheduled: { cadence?: string };
  report_viewed: {
    variant: 'built-in' | 'custom';
    report_type?: string;
    source?: string;
    is_guest?: boolean;
  };
  report_creator_prompt_clicked: { suggestion_label: string };
  report_customize_clicked: EmptyProps;

  // routines
  routine_creation_started: { entry_point: 'routines_list' | 'home_starter' };
  routine_draft_shown: { thread_id?: string };
  routine_opened_in_chat: { source: 'list' | 'detail' };
  routine_run_triggered: { source: 'list' | 'detail'; bulk: boolean; count?: number };
  routine_status_changed: { status: string; bulk: boolean; count?: number };
  routine_deleted: { count: number; bulk: boolean };

  // tasks
  task_opened: { via: 'chat_card' | 'list_row' | 'deep_link' | 'home_panel' };
  task_updated: { field: string; via: 'panel' };
  task_created: { count: number; via: 'panel' | 'chat' };
  task_opened_in_chat: { via: 'new_chat' | 'chat' };
  task_panel_expanded: { expanded: boolean };

  // knowledge hub
  knowledge_file_created: { file_type?: string };
  /**
   * `source` marks saves made outside the Knowledge Hub pages (e.g. the AEO
   * settings "Tracked brands" section writes the same KB files); absent for
   * saves from the Knowledge Hub itself.
   */
  knowledge_file_updated: {
    standardized?: StandardizedKnowledgePage;
    source?: 'aeo_settings_tracked_brands';
  };
  knowledge_file_deleted: EmptyProps;
  knowledge_file_opened: {
    source: 'tree' | 'recent';
    standardized?: StandardizedKnowledgePage;
    folder?: string;
  };
  knowledge_file_edit_started: { standardized?: StandardizedKnowledgePage };
  /**
   * `status` comes straight from the seed endpoint, so it is left as a string
   * rather than a closed union — a status the server adds should show up in
   * PostHog, not vanish. Known values: `written`, `skipped_custom`,
   * `skipped_no_domain`, `rejected_invalid_output`, `rejected_low_confidence`,
   * `rejected_ungrounded`, `rejected_low_contrast`.
   */
  brand_design_seeded: { status?: string; forced?: boolean };

  // skills
  skill_created: { skill_slug?: string };
  skill_installed: { skill_slug?: string; source: 'dialog' };
  skill_uninstalled: { skill_slug?: string };
  skill_updated: { skill_slug?: string };
  skill_deleted: { skill_slug?: string };
  skill_tried_in_chat: {
    skill_slug?: string;
    via: 'skills_page' | 'directory';
  };

  // glossary
  glossary_entry_opened: { category?: string; via: 'card' | 'related' };

  // integrations
  integration_connect_started: {
    provider: string;
    auth_type: IntegrationAuthType;
  };
  integration_connected: { provider: string; auth_type: IntegrationAuthType };
  integration_connect_failed: {
    provider: string;
    auth_type: IntegrationAuthType;
    error_code?: string;
  };
  integration_reauthorized: { provider: string };
  integration_dataset_toggled: { provider: string; enabled: boolean };
  integration_paused: { provider: string; paused: boolean };
  integration_set_default: { provider: string };
  integration_renamed: { provider: string };
  integration_disconnected: { provider: string };
  integration_configured: {
    provider: string;
    config_type: 'app_config' | 'api_key';
  };

  // settings
  member_invited: { invite_count: number; role?: string };
  organization_updated: EmptyProps;
  notification_channel_toggled: { channel: 'slack'; enabled: boolean };

  // global chrome / navigation
  breadcrumb_clicked: { level: 'home' | 'ancestor' | 'parent' };
  sidebar_toggled: { collapsed: boolean };

  // agents
  agent_installed: { agent_slug: string };
  agent_workflow_triggered: { agent_slug?: string; workflow_id?: string };
  agent_config_saved: { agent_slug?: string };
  agent_approval_actioned: {
    agent_slug?: string;
    action: 'approved' | 'rejected';
    count?: number;
  };
  agent_export_downloaded: { agent_slug?: string; format: 'xlsx' | 'pdf' };
  aeo_view_changed: { view: string };
}

/**
 * Event → feature area. The mapped type makes this exhaustive: adding an event
 * to ANALYTICS_EVENTS without adding it here fails type-check.
 */
export const EVENT_FEATURE_AREA: Record<AnalyticsEventName, FeatureArea> = {
  // Fallbacks only: both list events require a `feature_area` property, and
  // `track()` lets that explicit value override this map. These entries exist
  // because the Record is exhaustive, and are never the value that ships.
  list_searched: FEATURE_AREAS.navigation,
  list_filter_changed: FEATURE_AREAS.navigation,

  onboarding_started: FEATURE_AREAS.onboarding,
  onboarding_step_completed: FEATURE_AREAS.onboarding,
  onboarding_organization_created: FEATURE_AREAS.onboarding,
  onboarding_completed: FEATURE_AREAS.onboarding,

  user_signed_out: FEATURE_AREAS.auth,
  organization_switched: FEATURE_AREAS.auth,
  account_added: FEATURE_AREAS.auth,

  chat_message_sent: FEATURE_AREAS.chat,
  chat_thread_created: FEATURE_AREAS.chat,
  chat_file_attached: FEATURE_AREAS.chat,
  chat_dictation_used: FEATURE_AREAS.chat,
  chat_slash_command_used: FEATURE_AREAS.chat,
  chat_mode_changed: FEATURE_AREAS.chat,
  chat_tool_approved: FEATURE_AREAS.chat,
  chat_tool_rejected: FEATURE_AREAS.chat,
  chat_turn_stalled: FEATURE_AREAS.chat,
  chat_auth_retry: FEATURE_AREAS.chat,
  chat_generation_stopped: FEATURE_AREAS.chat,
  chat_suggestion_clicked: FEATURE_AREAS.chat,
  chat_thread_renamed: FEATURE_AREAS.chat,
  chat_thread_deleted: FEATURE_AREAS.chat,
  chat_new_chat_clicked: FEATURE_AREAS.chat,

  inbox_card_opened: FEATURE_AREAS.home,
  inbox_card_opened_in_chat: FEATURE_AREAS.home,
  inbox_card_dismissed: FEATURE_AREAS.home,
  inbox_overflow_toggled: FEATURE_AREAS.home,
  inbox_panel_expanded: FEATURE_AREAS.home,
  inbox_filter_changed: FEATURE_AREAS.home,
  inbox_sort_changed: FEATURE_AREAS.home,
  inbox_show_read_toggled: FEATURE_AREAS.home,

  report_created: FEATURE_AREAS.reports,
  report_renamed: FEATURE_AREAS.reports,
  report_deleted: FEATURE_AREAS.reports,
  report_exported: FEATURE_AREAS.reports,
  report_run_switched: FEATURE_AREAS.reports,
  report_scheduled: FEATURE_AREAS.reports,
  report_viewed: FEATURE_AREAS.reports,
  report_creator_prompt_clicked: FEATURE_AREAS.reports,
  report_customize_clicked: FEATURE_AREAS.reports,

  routine_creation_started: FEATURE_AREAS.routines,
  routine_draft_shown: FEATURE_AREAS.routines,
  routine_opened_in_chat: FEATURE_AREAS.routines,
  routine_run_triggered: FEATURE_AREAS.routines,
  routine_status_changed: FEATURE_AREAS.routines,
  routine_deleted: FEATURE_AREAS.routines,

  task_opened: FEATURE_AREAS.tasks,
  task_updated: FEATURE_AREAS.tasks,
  task_created: FEATURE_AREAS.tasks,
  task_opened_in_chat: FEATURE_AREAS.tasks,
  task_panel_expanded: FEATURE_AREAS.tasks,

  knowledge_file_created: FEATURE_AREAS.knowledge_hub,
  knowledge_file_updated: FEATURE_AREAS.knowledge_hub,
  knowledge_file_deleted: FEATURE_AREAS.knowledge_hub,
  knowledge_file_opened: FEATURE_AREAS.knowledge_hub,
  knowledge_file_edit_started: FEATURE_AREAS.knowledge_hub,
  brand_design_seeded: FEATURE_AREAS.knowledge_hub,

  skill_created: FEATURE_AREAS.skills,
  skill_installed: FEATURE_AREAS.skills,
  skill_uninstalled: FEATURE_AREAS.skills,
  skill_updated: FEATURE_AREAS.skills,
  skill_deleted: FEATURE_AREAS.skills,
  skill_tried_in_chat: FEATURE_AREAS.skills,

  glossary_entry_opened: FEATURE_AREAS.glossary,

  integration_connect_started: FEATURE_AREAS.integrations,
  integration_connected: FEATURE_AREAS.integrations,
  integration_connect_failed: FEATURE_AREAS.integrations,
  integration_reauthorized: FEATURE_AREAS.integrations,
  integration_dataset_toggled: FEATURE_AREAS.integrations,
  integration_paused: FEATURE_AREAS.integrations,
  integration_set_default: FEATURE_AREAS.integrations,
  integration_renamed: FEATURE_AREAS.integrations,
  integration_disconnected: FEATURE_AREAS.integrations,
  integration_configured: FEATURE_AREAS.integrations,

  member_invited: FEATURE_AREAS.settings,
  organization_updated: FEATURE_AREAS.settings,
  notification_channel_toggled: FEATURE_AREAS.settings,

  breadcrumb_clicked: FEATURE_AREAS.navigation,
  sidebar_toggled: FEATURE_AREAS.navigation,

  agent_installed: FEATURE_AREAS.agents,
  agent_workflow_triggered: FEATURE_AREAS.agents,
  agent_config_saved: FEATURE_AREAS.agents,
  agent_approval_actioned: FEATURE_AREAS.agents,
  agent_export_downloaded: FEATURE_AREAS.agents,
  aeo_view_changed: FEATURE_AREAS.agents,
};
